import React, { useEffect, useMemo, useRef, useState } from 'react';

/* Greetings in the languages most spoken by people sitting the Life in the UK
   test. Order is deliberate: English first so the line reads as a greeting
   before it reads as a language list, then rotating by rough population share
   of test-takers.

   `label` is the greeting as written; `lang` is the BCP-47 tag so screen
   readers and font fallback treat the script correctly. */
const GREETINGS = [
  { label: 'Hello',    lang: 'en' },
  { label: 'नमस्ते',     lang: 'hi' },   // Hindi
  { label: 'السلام علیکم', lang: 'ur' },  // Urdu
  { label: 'নমস্কার',    lang: 'bn' },   // Bengali
  { label: 'Bawo ni',  lang: 'yo' },   // Yoruba
  { label: '你好',      lang: 'zh' },   // Mandarin
  { label: 'Kem cho',  lang: 'gu' },   // Gujarati
  { label: 'Xin chào', lang: 'vi' },   // Vietnamese
  { label: 'Merhaba',  lang: 'tr' },   // Turkish
  { label: 'Kamusta',  lang: 'tl' },   // Tagalog
  { label: 'Cześć',    lang: 'pl' },   // Polish
  // Romanised, so it is tagged as such: `fa-Latn` keeps it out of the cursive
  // path below, which is only for text actually written in Arabic script.
  { label: 'Salaam',   lang: 'fa-Latn' },
];

const LETTER_STAGGER_MS = 55;   // gap between letters appearing
const LETTER_ANIM_MS = 380;     // must match greet-letter-in in index.css
const HOLD_MS = 1500;           // how long a completed word rests

/**
 * Cycles a greeting, revealing it one letter at a time, then swapping
 * language. Purely decorative: it renders a stable `aria-label` so assistive
 * tech announces "Hello" once rather than narrating every letter.
 *
 * Motion is opacity + translateY only. It deliberately avoids 3D transforms
 * (perspective / preserve-3d), which previously caused mobile crashes when
 * applied across many nodes — see docs/seo-and-funnel-state.md.
 */
export default function Greeting({ className = '' }) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  // Respect the OS reduced-motion setting. Read once on mount; if the user
  // prefers reduced motion the greeting still cycles, it just doesn't stagger.
  const reducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const current = GREETINGS[index];

  // Cursive scripts (Arabic/Nastaliq for Urdu) join their letters. Wrapping
  // each one in an inline-block box severs those joins and renders isolated
  // letterforms, which reads as broken text to anyone who can actually read it.
  // Those greetings fade in as one word instead of letter by letter — the
  // effect is preserved, the writing system is not damaged.
  const isCursive = current.lang === 'ur' || current.lang === 'fa';
  // (fa-Latn is romanised and animates letter by letter like any Latin word.)

  // Split into GRAPHEME CLUSTERS, not code points. Array.from() iterates code
  // points, which tears combining marks off their base consonant — "नमस्ते"
  // renders as "नमस ् त े" with the virama and vowel signs floating free, and
  // Arabic-script greetings lose their joining forms entirely. Intl.Segmenter
  // keeps each written character whole.
  const letters = useMemo(() => {
    if (isCursive) return [current.label];
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      const segmenter = new Intl.Segmenter(current.lang, { granularity: 'grapheme' });
      return Array.from(segmenter.segment(current.label), (s) => s.segment);
    }
    return Array.from(current.label);
  }, [current.label, current.lang, isCursive]);

  useEffect(() => {
    // Full cycle = every letter staggered in, the last letter's animation,
    // then the hold. Reduced motion skips straight to the hold.
    const cycleMs = reducedMotion
      ? HOLD_MS + LETTER_ANIM_MS
      : letters.length * LETTER_STAGGER_MS + LETTER_ANIM_MS + HOLD_MS;

    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % GREETINGS.length);
    }, cycleMs);

    return () => clearTimeout(timerRef.current);
  }, [index, letters.length, reducedMotion]);

  return (
    <span
      className={className}
      // The greeting is decorative motion around a single word; announce the
      // word, not the individual spans it is built from.
      aria-label={current.label}
      lang={current.lang}
    >
      {letters.map((char, i) => (
        <span
          // Keying on the word as well as the position restarts the animation
          // when the language changes.
          key={`${current.lang}-${i}`}
          className="greet-letter"
          aria-hidden="true"
          style={{
            animationDelay: reducedMotion ? '0ms' : `${i * LETTER_STAGGER_MS}ms`,
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
}
